import {
	fallbackToEmptyObject,
	type FormIdentifier,
	type FormIdentifierValue,
	type FormSubmission,
	type FormSubmissionFieldValues,
	type FormSubmissionFieldValuesAttachment,
	type Stored,
	submittedAtModelSortFn,
} from "@overmap-ai/common"
import {
	restructureSelectorWithArgs,
	selectFormIdentifierValues,
	selectFormSubmissionFieldValues,
	selectFormSubmissionFieldValuesAttachments,
} from "@overmap-ai/core"
import { createSelector } from "@reduxjs/toolkit"

import type { RootSelectorWithArgs } from "../typings"

export const selectLatestFormSubmissionFieldValuesOfFormSubmission: RootSelectorWithArgs<
	FormSubmission["uuid"],
	Stored<FormSubmissionFieldValues> | undefined
> = restructureSelectorWithArgs(
	createSelector(
		[selectFormSubmissionFieldValues, (_, formSubmissionId: FormSubmission["uuid"]) => formSubmissionId],
		(formSubmissionFieldValues, formSubmissionId) => {
			return formSubmissionFieldValues
				.filter(({ form_submission }) => form_submission === formSubmissionId)
				.toSorted(submittedAtModelSortFn)
				.pop()
		},
	),
)

export const selectLatestFieldValuesByFormSubmission = createSelector(
	[selectFormSubmissionFieldValues],
	(allFieldValues) => {
		const ret: Record<FormSubmission["uuid"], Stored<FormSubmissionFieldValues> | undefined> = {}

		for (const fieldValues of allFieldValues) {
			const { form_submission } = fieldValues

			const current = ret[form_submission]

			if (!current || fieldValues.submitted_at > current.submitted_at) {
				ret[form_submission] = fieldValues
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestIdentifierValuesByFormSubmission = createSelector(
	[selectFormIdentifierValues],
	(formIdentifierValues) => {
		const ret: Record<FormSubmission["uuid"], Record<FormIdentifier["uuid"], Stored<FormIdentifierValue>>> = {}

		for (const identifierValue of formIdentifierValues) {
			const { form_submission, form_identifier } = identifierValue

			if (!ret[form_submission]) ret[form_submission] = {}

			const current = ret[form_submission][form_identifier]

			if (!current || current.submitted_at <= identifierValue.submitted_at) {
				ret[form_submission][form_identifier] = identifierValue
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectAttachmentsByFormSubmissionFieldValues = createSelector(
	[selectFormSubmissionFieldValuesAttachments],
	(attachments) => {
		const ret: Record<FormSubmission["uuid"], Stored<FormSubmissionFieldValuesAttachment>[]> = {}

		for (const attachment of attachments) {
			const { field_values } = attachment
			if (!(field_values in ret)) ret[field_values] = []
			ret[field_values]!.push(attachment)
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestFormIdentifierValueOfFormSubmission = restructureSelectorWithArgs(
	createSelector(
		[
			selectFormIdentifierValues,
			(_, formSubmissionId: FormSubmission["uuid"]) => formSubmissionId,
			(_, __: FormSubmission["uuid"], formIdentifierId: FormIdentifier["uuid"]) => formIdentifierId,
		],
		(formIdentifierValues, formSubmissionId, formIdentifierId): Stored<FormIdentifierValue> | undefined => {
			return formIdentifierValues
				.filter(
					({ form_submission, form_identifier }) =>
						form_submission === formSubmissionId && form_identifier === formIdentifierId,
				)
				.toSorted(submittedAtModelSortFn)
				.pop()
		},
	),
)
